import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Snackbar,
  CircularProgress,
  Container,
  Paper,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { makeContactRequest } from "../../../Services/ContactUsAPi";

const ContactUsPage = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState({ open: false, type: "success", text: "" });

  const handleChange = (key) => (e) => {
    setForm((s) => ({ ...s, [key]: e.target.value }));
  };

  const validate = () => {
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setFeedback({ open: true, type: "error", text: "الاسم، البريد والرسالة مطلوبة." });
      return false;
    }
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(form.email)) {
      setFeedback({ open: true, type: "error", text: "الرجاء إدخال بريد إلكتروني صالح." });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        address: form.address,
        message: form.message,
      };
      const res = await makeContactRequest(payload);
      // assume API returns success message or status
      setFeedback({ open: true, type: "success", text: res?.message || "تم إرسال الرسالة بنجاح." });
      setForm({ name: "", email: "", phone: "", address: "", message: "" });
    } catch (err) {
      console.error("Contact request error:", err);
      const msg = err?.response?.data?.message || "حدث خطأ أثناء إرسال الرسالة.";
      setFeedback({ open: true, type: "error", text: msg });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseFeedback = () => setFeedback((f) => ({ ...f, open: false }));

  return (
    <Box
      sx={{
        py: 10,
        background: "linear-gradient(135deg, #f5f7fa 0%, #e8eef5 100%)",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage:
            "radial-gradient(circle, rgba(102, 126, 234, 0.03) 1px, transparent 1px)",
          backgroundSize: "30px 30px",
          pointerEvents: "none",
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Box sx={{ textAlign: "center", mb: 8 }}>
          <Typography
            variant="h3"
            gutterBottom
            fontWeight={800}
            sx={{
              background: "linear-gradient(135deg, #f36f21 0%, #e65100 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              mb: 2,
            }}
          >
            تواصل معنا
          </Typography>
          <Typography
            variant="h6"
            sx={{ color: "text.secondary", maxWidth: 600, mx: "auto" }}
          >
            نحن هنا للإجابة على استفساراتك ومساعدتك في أي وقت
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Contact Info */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: 4,
                background: "white",
                border: "1px solid #e0e0e0",
                height: "100%",
              }}
            >
              <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
                معلومات التواصل
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      background: "linear-gradient(135deg, #f36f21 0%, #e65100 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <EmailIcon sx={{ color: "white" }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                      البريد الإلكتروني
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      info@restocash.com
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      background: "linear-gradient(135deg, #f36f21 0%, #e65100 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <PhoneIcon sx={{ color: "white" }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                      رقم الهاتف
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      +966 50 123 4567
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      background: "linear-gradient(135deg, #f36f21 0%, #e65100 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <LocationOnIcon sx={{ color: "white" }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                      العنوان
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      الرياض، المملكة العربية السعودية
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  mt: 4,
                  p: 3,
                  borderRadius: 3,
                  background: "linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)",
                }}
              >
                <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
                  💡 <strong>نصيحة:</strong> سنرد على رسالتك خلال 24 ساعة من أيام العمل
                </Typography>
              </Box>
            </Paper>
          </Grid>

          {/* Contact Form */}
          <Grid item xs={12} md={8}>
            <Paper
              elevation={0}
              sx={{
                p: 5,
                borderRadius: 4,
                background: "white",
                border: "1px solid #e0e0e0",
              }}
            >
              <Typography variant="h5" fontWeight={700} sx={{ mb: 4 }}>
                أرسل رسالتك
              </Typography>

              <Box component="form" onSubmit={handleSubmit} noValidate>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="الاسم الكامل"
                      value={form.name}
                      onChange={handleChange("name")}
                      fullWidth
                      required
                      autoComplete="name"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          "&:hover fieldset": {
                            borderColor: "#f36f21",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#f36f21",
                            borderWidth: 2,
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="البريد الإلكتروني"
                      value={form.email}
                      onChange={handleChange("email")}
                      fullWidth
                      required
                      autoComplete="email"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          "&:hover fieldset": {
                            borderColor: "#f36f21",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#f36f21",
                            borderWidth: 2,
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="رقم الهاتف"
                      value={form.phone}
                      onChange={handleChange("phone")}
                      fullWidth
                      autoComplete="tel"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          "&:hover fieldset": {
                            borderColor: "#f36f21",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#f36f21",
                            borderWidth: 2,
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="العنوان (اختياري)"
                      value={form.address}
                      onChange={handleChange("address")}
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          "&:hover fieldset": {
                            borderColor: "#f36f21",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#f36f21",
                            borderWidth: 2,
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="رسالتك"
                      value={form.message}
                      onChange={handleChange("message")}
                      fullWidth
                      required
                      multiline
                      rows={5}
                      placeholder="اكتب رسالتك هنا..."
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          "&:hover fieldset": {
                            borderColor: "#f36f21",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#f36f21",
                            borderWidth: 2,
                          },
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={loading}
                      endIcon={loading ? null : <SendIcon />}
                      startIcon={
                        loading ? <CircularProgress color="inherit" size={20} /> : null
                      }
                      sx={{
                        py: 1.8,
                        px: 5,
                        fontWeight: 700,
                        fontSize: "1.05rem",
                        borderRadius: 3,
                        textTransform: "none",
                        background: "linear-gradient(135deg, #f36f21 0%, #e65100 100%)",
                        boxShadow: "0 8px 20px rgba(243, 111, 33, 0.3)",
                        "&:hover": {
                          background: "linear-gradient(135deg, #e65100 0%, #d84315 100%)",
                          boxShadow: "0 12px 28px rgba(243, 111, 33, 0.4)",
                        },
                        "&:disabled": {
                          background: "rgba(243, 111, 33, 0.5)",
                        },
                      }}
                    >
                      {loading ? "جاري الإرسال..." : "إرسال الرسالة"}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={handleCloseFeedback}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseFeedback}
          severity={feedback.type}
          sx={{
            width: "100%",
            borderRadius: 2,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          }}
        >
          {feedback.text}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactUsPage;
